/**
 * App-wide data: one place that owns the store, API, repos and sync, exposed through hooks.
 * Pages never touch storage or fetch directly; they use the hooks below.
 */
import { updateSettings, type Settings } from "@hexling/core";
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Api } from "../lib/api";
import type { AuthClient, Me } from "../lib/auth";
import { tokenStore } from "../lib/platform";
import type { LocalStore } from "../storage/types";
import { ContentIndex, loadCachedContent, refreshContent } from "./content";
import type { SyncEngine } from "./sync";
import type { UserRepo } from "./user";
import { type Profile } from "./user";

export interface AppServices {
  store: LocalStore;
  api: Api;
  auth: AuthClient | null;
  user: UserRepo;
  sync: SyncEngine;
}

const Services = createContext<AppServices | null>(null);

export function useServices(): AppServices {
  const s = useContext(Services);
  if (!s) throw new Error("useServices must be used inside <AppDataProvider>");
  return s;
}

/** Applies theme and motion settings to <html> so CSS can react. */
export function applySettingsToDocument(
  settings: Settings,
  root: HTMLElement = document.documentElement,
) {
  root.dataset.theme = settings.theme;
  root.dataset.reducedMotion = String(settings.reducedMotion);
}

const SYNC_DEBOUNCE_MS = 2_000;
const SYNC_INTERVAL_MS = 5 * 60_000;

export function AppDataProvider({
  services,
  children,
  queryClient,
}: {
  services: AppServices;
  children: ReactNode;
  queryClient?: QueryClient;
}) {
  const client = useMemo(
    () =>
      queryClient ??
      new QueryClient({ defaultOptions: { queries: { staleTime: Infinity, retry: false } } }),
    [queryClient],
  );
  return (
    <QueryClientProvider client={client}>
      <Services.Provider value={services}>
        <Background />
        {children}
      </Services.Provider>
    </QueryClientProvider>
  );
}

/** Side effects: keep content fresh, mirror settings to <html>, and sync when appropriate. */
function Background() {
  const { user, sync, store, api } = useServices();
  const qc = useQueryClient();
  const profile = useProfile().data;
  const me = useMe().data;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refresh content from the network once per launch (cached copy is shown meanwhile).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const current = await loadCachedContent(store);
      try {
        const next = await refreshContent(store, api, current);
        if (!cancelled && next !== current) qc.setQueryData(["content"], new ContentIndex(next));
      } catch {
        /* offline: keep using the cached library */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [store, api, qc]);

  useEffect(() => {
    if (profile) applySettingsToDocument(profile.settings);
  }, [profile]);

  const canSync = Boolean(me?.user && profile?.settings.syncEnabled);

  useEffect(() => {
    if (!canSync) return;
    const run = () =>
      sync
        .sync()
        .then(() => qc.invalidateQueries({ queryKey: ["user"] }))
        .then(() => qc.invalidateQueries({ queryKey: ["sync"] }))
        .catch(() => qc.invalidateQueries({ queryKey: ["sync"] }));
    run();
    const off = user.onChange(() => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(run, SYNC_DEBOUNCE_MS);
    });
    const interval = setInterval(run, SYNC_INTERVAL_MS);
    window.addEventListener("online", run);
    return () => {
      off();
      clearInterval(interval);
      window.removeEventListener("online", run);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [canSync, sync, user, qc]);

  // Any local change refreshes the matching queries.
  useEffect(() => user.onChange(() => qc.invalidateQueries({ queryKey: ["user"] })), [user, qc]);
  return null;
}

// ---------------------------------------------------------------- hooks

/** The content library index, from the device cache (null until anything has been downloaded). */
export function useContent() {
  const { store } = useServices();
  return useQuery({
    queryKey: ["content"],
    queryFn: async () => {
      const cached = await loadCachedContent(store);
      return cached ? new ContentIndex(cached) : null;
    },
  });
}

export function useProfile() {
  const { user } = useServices();
  return useQuery<Profile>({ queryKey: ["user", "profile"], queryFn: () => user.profile() });
}

/** Settings with sensible defaults while loading. */
export function useSettings(): Settings | undefined {
  return useProfile().data?.settings;
}

export function useMe() {
  const { api, auth } = useServices();
  return useQuery<Me>({
    queryKey: ["me"],
    queryFn: async () => {
      if (!auth) return { user: null };
      try {
        return await api.get<Me>("/api/me");
      } catch {
        return { user: null };
      }
    },
  });
}

export function useNotes() {
  const { user } = useServices();
  return useQuery({ queryKey: ["user", "notes"], queryFn: () => user.notes() });
}

export function useLists() {
  const { user } = useServices();
  return useQuery({ queryKey: ["user", "lists"], queryFn: () => user.lists() });
}

export function useListItems(listId?: string) {
  const { user } = useServices();
  return useQuery({
    queryKey: ["user", "listItems", listId ?? "all"],
    queryFn: () => user.listItems(listId),
  });
}

export function useBookmarks() {
  const { user } = useServices();
  return useQuery({ queryKey: ["user", "bookmarks"], queryFn: () => user.bookmarks() });
}

export function useLastSynced() {
  const { sync } = useServices();
  return useQuery({ queryKey: ["sync", "last"], queryFn: () => sync.lastSyncedAt() });
}

export function useDeviceId() {
  const { user } = useServices();
  return useQuery({ queryKey: ["device"], queryFn: () => user.deviceId() });
}

/**
 * Settings for editing screens. Changes apply to local React state synchronously inside the
 * event handler, so native radios and selects never flicker back, then persist to storage.
 */
export function useSettingsEditor(): {
  settings: Settings;
  update: (patch: SettingsPatch) => Promise<void>;
} {
  const { user } = useServices();
  const qc = useQueryClient();
  const stored = useProfile().data!.settings;
  const [local, setLocal] = useState(stored);
  useEffect(() => setLocal(stored), [stored]);
  const update = async (patch: SettingsPatch) => {
    setLocal((cur) => updateSettings(cur, patch));
    const current = qc.getQueryData<Profile>(["user", "profile"]);
    if (current)
      qc.setQueryData<Profile>(["user", "profile"], {
        ...current,
        settings: updateSettings(current.settings, patch),
      });
    await user.updateSettings(patch);
  };
  return { settings: local, update };
}

type SettingsPatch = Parameters<typeof updateSettings>[1];

/** Sign out everywhere this device knows about, keeping local notes. */
export function useSignOut() {
  const { auth } = useServices();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  return {
    busy,
    signOut: async () => {
      setBusy(true);
      try {
        await auth?.signOut();
      } finally {
        tokenStore.set(null);
        await qc.invalidateQueries({ queryKey: ["me"] });
        setBusy(false);
      }
    },
  };
}
